import { Autocomplete, TextField } from "@mui/material"
import { ColDef, GridApi, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { LoadNewOrganizations } from "../../actions"
import { setBackHandler, setHeaderCount, setHeaderTitle, setRefreshHandler } from "../../helpers/header.helper"
import setToast from "../../helpers/toaster.helper"
import { PhoneHostImportReportSummary, mapToPhoneHostImportReportSummary } from "../../models/phone-host-import-report.model"
import { OrganizationModel } from "../../models/organization.model"
import { PhoneNumberService } from "../../services/PhonenumberService"
import { UtilityService } from "../../services/UtilityService"
import * as selector from "../../selectors/selectors"
import { AppRoutes } from "../routes/route.path"
import { GridStatusLabelVariant, ToasterTexts, emptyLabels, pageTitles } from "../shared/constants"
import GridComponent from "../shared/grid"
import { dateComparator } from "../shared/grid/grid.services"
import { StatusRenderer } from "../shared/grid/statusRenderer"
import PageHeaderComponent from "../shared/page-header.component"
import { ToasterType } from "../shared/toaster/toaster.model"
import PhoneHostImportReportDetail from "./phone-host-import-report-detail.component"

const statusVariant = (status: string) => {
    const value = (status || "").toLowerCase()
    if (value === "completed") {
        return GridStatusLabelVariant.Active
    }
    if (value === "failed") {
        return GridStatusLabelVariant.Danger
    }
    if (value === "incomplete") {
        return GridStatusLabelVariant.Warning
    }
    return GridStatusLabelVariant.Other
}

const PhoneHostImportReports = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const Organizations: OrganizationModel[] = useSelector(selector.newOrganizations)

    const [gridApi, setGridApi] = useState<GridApi>(null)
    const gridApiRef = useRef<GridApi>(null)
    const [reports, setReports] = useState<PhoneHostImportReportSummary[]>([])
    const [selectedOrgId, setSelectedOrgId] = useState<string>("")
    const [searchValue, setSearchValue] = useState<string>("")
    const [selectedReportId, setSelectedReportId] = useState<string>("")
    const [detailOpen, setDetailOpen] = useState<boolean>(false)

    const organizationOptions = (Organizations || []).filter((org) => org.Name !== null && !org.IsDeleted)
    const selectedOrganization = organizationOptions.find((org) => org.ID === selectedOrgId) || null

    const columnDefs: ColDef[] = [
        {
            field: "FileName",
            headerName: "File Name",
            valueGetter: (params: ValueGetterParams) => params.data?.FileName || "--",
            minWidth: 220,
            resizable: true
        },
        {
            field: "Status",
            headerName: "Status",
            valueGetter: (params: ValueGetterParams) => params.data?.Status || "--",
            cellRendererSelector: (params: ICellRendererParams) => {
                if (!params.data) return undefined
                return {
                    component: StatusRenderer,
                    params: {
                        Variant: statusVariant(params.data.Status),
                        Label: params.data.Status || "--",
                        Id: params.data.Id
                    }
                }
            },
            minWidth: 160,
            resizable: true
        },
        {
            field: "SavedCount",
            headerName: "Saved",
            valueGetter: (params: ValueGetterParams) => params.data?.SavedCount ?? 0,
            minWidth: 120,
            resizable: true
        },
        {
            field: "IgnoredCount",
            headerName: "Ignored",
            valueGetter: (params: ValueGetterParams) => params.data?.IgnoredCount ?? 0,
            minWidth: 120,
            resizable: true
        },
        {
            field: "CreatedBy",
            headerName: "Imported By",
            valueGetter: (params: ValueGetterParams) => params.data?.CreatedBy || "--",
            minWidth: 180,
            resizable: true
        },
        {
            field: "CreatedDateTime",
            headerName: "Created Date",
            valueFormatter: (params: ValueFormatterParams) => {
                return params.data?.CreatedDateTime ? UtilityService.convertToDateTimeFormat(params.data.CreatedDateTime) : "--"
            },
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.CreatedDateTime, nodeB.data.CreatedDateTime),
            sort: "desc",
            minWidth: 190,
            resizable: true
        }
    ]

    const loadReports = (organizationId: string) => {
        if (!organizationId) {
            setReports([])
            return
        }

        gridApiRef.current?.showLoadingOverlay()
        PhoneNumberService.getAllBulkImportReport(organizationId)
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data.map(mapToPhoneHostImportReportSummary) : []
                setReports(rows)
            })
            .catch(() => {
                setReports([])
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
            })
            .finally(() => {
                gridApiRef.current?.hideOverlay()
            })
    }

    useEffect(() => {
        setHeaderTitle(pageTitles.PHONE_NUMBER_IMPORT_REPORTS)
        setBackHandler(() => navigate(AppRoutes.NEW_PhONE_NUMBER))
        if (!Organizations?.length) {
            LoadNewOrganizations(dispatch)
        }

        const state = location.state as { OrganizationID?: string } | null
        if (state?.OrganizationID) {
            setSelectedOrgId(state.OrganizationID)
        }

        return () => {
            setBackHandler()
            setRefreshHandler()
            setHeaderCount(0)
        }
    }, [])

    useEffect(() => {
        setHeaderCount(reports.length)
    }, [reports.length])

    useEffect(() => {
        setRefreshHandler(() => loadReports(selectedOrgId))
        loadReports(selectedOrgId)
    }, [selectedOrgId])

    const searchReports = (event) => {
        setSearchValue(event.target.value)
        gridApi?.setQuickFilter(event.target.value)
    }

    return (
        <div className="d-flex flex-direction h-p-100 o-hidden">
            <PageHeaderComponent
                GridTitle=""
                Count={reports.length}
                onSearch={searchReports}
                searchValue={searchValue}
                PlaceHolder={IsMobileDevice ? "Search..." : "Find a report..."}
            />
            <div className="px-2 pb-1 mt-1" style={{ maxWidth: 420 }}>
                <Autocomplete
                    size="small"
                    options={organizationOptions}
                    getOptionLabel={(option) => option?.Name || ""}
                    classes={{ input: "h2 color-text-field-font", paper: "bg-color-menu-bg box-shadow-none dialogue-shadow" }}
                    onChange={(_event, value) => setSelectedOrgId(value?.ID || "")}
                    value={selectedOrganization}
                    isOptionEqualToValue={(option, value) => option.ID === value.ID}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select organization"
                            variant="outlined"
                        />
                    )}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "0.75rem"
                        }
                    }}
                />
            </div>
            <div className="flex-1 o-hidden">
                <GridComponent
                    ColumnDefs={columnDefs}
                    RowData={reports}
                    OnGridReady={(params) => {
                        gridApiRef.current = params.api
                        setGridApi(params.api)
                    }}
                    OnGridRowClick={(row: PhoneHostImportReportSummary) => {
                        setSelectedReportId(row.Id)
                        setDetailOpen(true)
                    }}
                    IsPaginationEnabled={true}
                    EmptyLabel={selectedOrgId ? emptyLabels.REPORTS : "Select an organization to view import reports"}
                    LockVisible={IsMobileDevice}
                    gridApi={gridApi}
                    isHorizontalScroll={true}
                    ShowRowPointer={true}
                    GetRowId={(params) => params.data.Id}
                />
            </div>
            {detailOpen && !!selectedReportId && (
                <PhoneHostImportReportDetail
                    Open={detailOpen}
                    ReportId={selectedReportId}
                    OnClose={() => {
                        setDetailOpen(false)
                        setSelectedReportId("")
                    }}
                />
            )}
        </div>
    )
}

export default PhoneHostImportReports
